// Read/navigation bridges for accounting review surfaces. They expose bounded
// page state and ordinary navigation only; filing, approval and posting remain
// in the existing reviewed workflows.
const pageControlFinite = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const PAGE_CONTROL_ACCOUNTING_INCOMPLETE = 'accounting.incompleteBasis';
const pageControlPeriodPatch = (current, fields) => {
  const next = { ...current };
  if (fields.fromDate !== undefined) next.from = fields.fromDate;
  if (fields.toDate !== undefined) next.to = fields.toDate;
  if (fields.months !== undefined) {
    if (!Array.isArray(fields.months) || fields.months.length !== 2 || fields.months.some((month) => !Number.isInteger(month) || month < 0 || month > 11) || fields.months[0] > fields.months[1]) throw new Error('pageControl.period');
    next.months = fields.months;
  }
  return next;
};

function useReportPageControlAdapter(c) {
  usePageOwnedControlAdapter(c.page, {
    read: () => {
      if (c.blocked) return { blocked: true, blockReason: 'accounting_basis_incomplete', accountingBasis: c.accountingBasis || null };
      const rows = c.rows();
      return {
        year: c.year, period: { from: c.filter.from || '', to: c.filter.to || '', months: c.filter.months },
        view: c.view, reportingBasis: c.reportingBasis || null, summary: c.summary(), truncated: rows.length > 150,
        accounts: rows.slice(0, 150).map(({ nr, name, amount, inn, endring, ut }) => ({ accountCode: String(nr), name, amount: pageControlFinite(amount), opening: pageControlFinite(inn), movement: pageControlFinite(endring), closing: pageControlFinite(ut) })),
      };
    },
    perform: async (step) => {
      if (c.blocked) throw new Error(PAGE_CONTROL_ACCOUNTING_INCOMPLETE);
      const f = step.fields || {};
      if (step.action === 'set_fields') {
        if (f.fromDate !== undefined || f.toDate !== undefined || f.months !== undefined) c.setFilter(pageControlPeriodPatch(c.filter, f));
        if (f.view !== undefined) {
          if (!c.views.includes(f.view)) throw new Error('pageControl.unsupported');
          c.setView(f.view);
        }
        return { result: 'fields_applied' };
      }
      if (step.action === 'open_record' && step.operation === 'open_account') {
        const row = c.rows().find((account) => String(account.nr) === String(f.accountCode || ''));
        if (!row) throw new Error('pageControl.recordNotFound');
        c.openAccount(row);
        return { result: 'account_opened', accountCode: String(row.nr) };
      }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useReconciliationPageControlAdapter(c) {
  usePageOwnedControlAdapter('reconciliation', {
    read: () => {
      if (!c.available()) return { available: false };
      return {
        available: true, periodId: c.periodId, view: c.view, locked: Boolean(c.period?.locked), progress: pageControlFinite(c.period?.progress),
        reportingBasis: c.period?.basisSource === 'imported_accounting_statistics' ? { kind: 'imported_accounting_statistics', label: 'Imported accounting statistics' } : null,
        areas: c.areas.map((area) => ({ id: area.key, label: area.label, status: c.areaStatuses[area.key] || 'ikke_kontrollert', ...c.counts(area) })),
        accounts: c.accounts.slice(0, 150).map(({ accountId, accountName, areaKey, bookedBalance, balance, difference, status }) => ({ id: accountId, name: accountName || '', areaId: areaKey || 'ovrig', balance: pageControlFinite(bookedBalance ?? balance), difference: pageControlFinite(difference), status: status || '' })),
      };
    },
    perform: async (step) => {
      if (!c.available()) throw new Error('pageControl.businessMismatch');
      const f = step.fields || {};
      if (step.action === 'set_fields' && f.periodId !== undefined) {
        if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(f.periodId)) throw new Error('pageControl.period');
        c.setPeriodId(f.periodId); return { result: 'period_selected', periodId: f.periodId };
      }
      if ((step.action === 'open_record' || step.action === 'open_review') && step.operation === 'open_area') {
        const area = c.areas.find((row) => row.key === f.areaId);
        if (!area) throw new Error('pageControl.recordNotFound');
        c.openArea(area.key); return { result: 'area_opened', areaId: area.key };
      }
      if ((step.action === 'open_record' || step.action === 'open_review') && step.operation === 'open_account') {
        const account = c.accounts.find((row) => String(row.accountId) === String(f.accountCode || ''));
        if (!account) throw new Error('pageControl.recordNotFound');
        c.openAccount(account.accountId); return { result: 'account_opened', accountCode: String(account.accountId) };
      }
      throw new Error('pageControl.reviewOnly');
    },
  });
}

function useVatFilingPageControlAdapter(c) {
  const exactTerm = (id) => c.terms.find((term) => String(term.key) === String(id || ''));
  usePageOwnedControlAdapter('vat_filing', {
    read: () => c.blocked ? { blocked: true, blockReason: 'accounting_basis_incomplete', accountingBasis: c.accountingBasis || null } : ({
      year: c.year, view: c.view, selectedTermId: c.selectedTerm?.key || '', vatRegistered: c.vatRegistered,
      totals: c.totals,
      terms: c.terms.slice(0, 20).map((term) => ({
        id: term.key, number: term.n, year: term.year, status: term.status, kind: term.kind,
        workflowState: c.workflowState(term),
        amount: pageControlFinite(term.amount), netVat: pageControlFinite(term.netVat), dueDate: term.dueDate || '', paymentCount: Array.isArray(term.payments) ? term.payments.length : 0,
        codes: (term.codes || []).slice(0, 30).map(({ code, desc, amount }) => ({ code: String(code), description: desc || '', amount: pageControlFinite(amount) })),
      })),
    }),
    perform: async (step) => {
      if (c.blocked) throw new Error(PAGE_CONTROL_ACCOUNTING_INCOMPLETE);
      const f = step.fields || {};
      if (step.action === 'set_fields' && f.view !== undefined) {
        if (!['terminer', 'oversikt'].includes(f.view)) throw new Error('pageControl.unsupported');
        c.setView(f.view); return { result: 'fields_applied' };
      }
      const term = exactTerm(f.termId);
      if (!term) throw new Error('pageControl.recordNotFound');
      if (step.action === 'open_record' && step.operation === 'open_vat_term') { c.openTerm(term); return { result: 'term_opened', termId: term.key }; }
      if (step.action === 'create_draft' && step.operation === 'prepare_vat_return') {
        if (c.workflowState(term) === 'levert') throw new Error('pageControl.alreadySubmitted');
        c.prepareReturn(term);
        return { result: 'vat_return_prepared', termId: term.key, requiresApproval: true, filingAvailable: false };
      }
      if (step.action === 'open_dialog' && step.operation === 'open_payment_dialog') { c.openTerm(term); c.openDialog('betaling'); return { result: 'payment_dialog_opened', termId: term.key }; }
      if (step.action === 'open_dialog' && step.operation === 'open_customs_dialog') { c.openTerm(term); c.openDialog('tolldekl'); return { result: 'customs_dialog_opened', termId: term.key }; }
      if (step.action === 'open_review' && step.operation === 'open_vat_basis') {
        const code = (term.codes || []).find((row) => String(row.code) === String(f.vatCode || ''));
        if (!code) throw new Error('pageControl.recordNotFound');
        c.openBasis(code, term); return { result: 'vat_basis_opened', termId: term.key, vatCode: String(code.code) };
      }
      throw new Error('pageControl.reviewOnly');
    },
  });
}

function usePayrollPageControlAdapter(c) {
  usePageOwnedControlAdapter('payroll', {
    read: () => {
      if (!c.access) return { access: false, page: c.page };
      return {
        access: true, page: c.page, employeeView: c.mode, selectedEmployeeId: c.employeeId || '',
        pages: c.pages.map(({ value, label }) => ({ id: value, label })),
        employees: c.employees.slice(0, 100).map(({ id, name, active, lonnstype }) => ({ id, name, active: active !== false, salaryType: lonnstype || '' })),
        runs: c.runs.slice(0, 50).map((run) => ({
          id: run.id, period: run.period || '', paymentDate: run.paymentDate || '', status: run.status || '', employeeCount: Array.isArray(run.lines) ? run.lines.length : 0,
          gross: pageControlFinite(run.gross ?? (run.lines || []).reduce((sum, line) => sum + pageControlFinite(line.grossSalary), 0)),
          tax: pageControlFinite(run.tax ?? (run.lines || []).reduce((sum, line) => sum + pageControlFinite(line.taxDeduction), 0)),
          net: pageControlFinite(run.net ?? (run.lines || []).reduce((sum, line) => sum + pageControlFinite(line.netPay), 0)),
        })),
      };
    },
    perform: async (step) => {
      if (!c.access) throw new Error('pageControl.access');
      const f = step.fields || {};
      if (step.action === 'set_fields' && f.payrollPage !== undefined) {
        if (!c.pages.some((page) => page.value === f.payrollPage)) throw new Error('pageControl.recordNotFound');
        c.setPage(f.payrollPage); return { result: 'payroll_page_opened', payrollPage: f.payrollPage };
      }
      if (step.action === 'open_record' && step.operation === 'open_employee') {
        const employee = c.employees.find((row) => String(row.id) === String(f.employeeId || ''));
        if (!employee) throw new Error('pageControl.recordNotFound');
        c.openEmployee(employee.id); return { result: 'employee_opened', employeeId: employee.id };
      }
      if (step.action === 'open_record' && step.operation === 'open_payroll_run') {
        const run = c.runs.find((row) => String(row.id) === String(f.payrollRunId || ''));
        if (!run) throw new Error('pageControl.recordNotFound');
        c.openRun(run.id); return { result: 'payroll_run_opened', payrollRunId: run.id };
      }
      throw new Error('pageControl.reviewOnly');
    },
  });
}

function useTaxReturnPageControlAdapter(c) {
  usePageOwnedControlAdapter('tax_return', {
    read: () => ({ year: c.year, phase: c.phase, activeSectionId: c.active, sections: c.steps.map(({ id, label }) => ({ id, label, completed: c.done.includes(id) })), readiness: c.model.readiness || {}, reviewRequired: c.model.reviewRequired === true, summary: c.summary }),
    perform: async (step) => {
      const id = step.fields?.sectionId;
      if (!['set_fields', 'open_record', 'open_review'].includes(step.action) || step.operation !== 'open_section') throw new Error('pageControl.reviewOnly');
      if (!c.steps.some((row) => row.id === id)) throw new Error('pageControl.recordNotFound');
      c.setPhase('draft'); c.setActive(id); return { result: 'section_opened', sectionId: id };
    },
  });
}

function useAnnualAccountsPageControlAdapter(c) {
  usePageOwnedControlAdapter('annual_accounts', {
    read: () => ({ year: c.year, activeSectionId: c.active, stage: c.stage, yearClosed: c.yearClosed, sections: c.steps.map(({ id, label }) => ({ id, label, completed: c.done.includes(id) })), noteCount: c.noteCount,
      noteDecisions: (c.noteDecisions || []).map(({ id, value, reasonCode, signal, missingFacts, productSupport }) => ({ id, value, reasonCode, signal: Boolean(signal), missingFacts, productSupport })),
      incomeTotal: pageControlFinite(c.incomeTotal), assetTotal: pageControlFinite(c.assetTotal), equityAndDebtTotal: pageControlFinite(c.equityAndDebtTotal) }),
    perform: async (step) => {
      if (step.action === 'set_fields' && step.operation === 'apply_note_suggestions') {
        window.PlummePendingAnnualNoteSuggestionYear = c.year;
        c.setActive('notes');
        setTimeout(() => window.dispatchEvent(new CustomEvent('plumme:annual-note-suggestions', { detail: { year: c.year } })), 0);
        return { result: 'annual_note_draft_updated', year: c.year };
      }
      const id = step.fields?.sectionId;
      if (!['set_fields', 'open_record', 'open_review'].includes(step.action) || step.operation !== 'open_section') throw new Error('pageControl.reviewOnly');
      if (!c.steps.some((row) => row.id === id)) throw new Error('pageControl.recordNotFound');
      c.setActive(id); return { result: 'section_opened', sectionId: id };
    },
  });
}

function useYearClosingPageControlAdapter(c) {
  usePageOwnedControlAdapter('year_closing', {
    read: () => c.blocked ? { blocked: true, blockReason: 'accounting_basis_incomplete', accountingBasis: c.accountingBasis || null } : ({
      year: c.year, mode: c.mode, yearClosed: c.yearClosed, canClose: c.canClose, reason: c.reason || '',
      summary: c.summary, checks: c.checks.slice(0, 100).map(({ id, label, detail, status, count, allowManualReview }) => ({ id, label, detail, status, count: pageControlFinite(count), allowManualReview: Boolean(allowManualReview) })),
    }),
    perform: async (step) => {
      if (c.blocked) throw new Error(PAGE_CONTROL_ACCOUNTING_INCOMPLETE);
      const f = step.fields || {}, check = c.checks.find((row) => String(row.id) === String(f.checkId || ''));
      if (!check) throw new Error('pageControl.recordNotFound');
      if (step.action === 'open_record' && step.operation === 'open_check') { c.setMode('review'); c.openCheck(check); return { result: 'check_opened', checkId: check.id }; }
      if ((step.action === 'open_review' || step.action === 'open_dialog') && step.operation === 'open_work_area') { c.setMode('review'); if (!c.openWorkArea(check)) throw new Error('pageControl.unsupported'); return { result: 'work_area_opened', checkId: check.id }; }
      throw new Error('pageControl.reviewOnly');
    },
  });
}

Object.assign(window, {
  useReportPageControlAdapter, useReconciliationPageControlAdapter, useVatFilingPageControlAdapter,
  usePayrollPageControlAdapter, useTaxReturnPageControlAdapter, useAnnualAccountsPageControlAdapter,
  useYearClosingPageControlAdapter,
});
